/**
 * Report Theme Service — Firestore source of truth, localStorage as cache.
 *
 * Resolution order at fetch time:
 *   user theme → user's company theme → DEFAULT_THEME
 *
 * Paths:
 *   per-user:    users/{uid}/settings/reportTheme
 *   per-company: companies/{cid}/settings/reportTheme
 */
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { authedFetch, createFetchAbortTimeout } from '../lib/authedFetch';
import { isHostedLogoMediaUrl, parseLogoStoragePathFromUrl } from '../lib/logoStorageUrl';
import { DEFAULT_THEME, type ReportTheme } from '../types/reportTheme';
import { normalizeCoverLayout } from '../lib/normalizeCoverLayout';

export const THEME_LOCAL_KEY = 'deepatomic_report_theme';

const userThemeRef = (uid: string) => doc(db, `users/${uid}/settings`, 'reportTheme');
const companyThemeRef = (cid: string) => doc(db, `companies/${cid}/settings`, 'reportTheme');

function normalizeTheme(theme: ReportTheme): ReportTheme {
  return {
    ...theme,
    coverLayout: normalizeCoverLayout(theme.coverLayout),
  };
}

/**
 * Fetch the effective theme for a user. Falls through user → company → DEFAULT.
 * Errors at any tier are logged and skipped (we degrade to the next tier).
 */
export async function fetchReportTheme(
  uid: string,
  companyId?: string | null,
): Promise<ReportTheme> {
  try {
    const userSnap = await getDoc(userThemeRef(uid));
    if (userSnap.exists()) {
      return normalizeTheme({ ...DEFAULT_THEME, ...(userSnap.data() as Partial<ReportTheme>) });
    }
  } catch (e) {
    console.warn('reportThemeService: user fetch failed', e);
  }

  if (companyId) {
    try {
      const companySnap = await getDoc(companyThemeRef(companyId));
      if (companySnap.exists()) {
        return normalizeTheme({ ...DEFAULT_THEME, ...(companySnap.data() as Partial<ReportTheme>) });
      }
    } catch (e) {
      console.warn('reportThemeService: company fetch failed', e);
    }
  }

  return normalizeTheme({ ...DEFAULT_THEME });
}

/**
 * Persist a theme to Firestore. For user scope, also mirrors to localStorage cache.
 * Company-scoped writes are gated by Firestore rules — non-admin members will see
 * a permission-denied error which the caller should surface to the user.
 */
export async function saveReportTheme(
  ownerId: string,
  theme: ReportTheme,
  scope: 'user' | 'company',
): Promise<void> {
  const ref = scope === 'company' ? companyThemeRef(ownerId) : userThemeRef(ownerId);
  await setDoc(ref, theme, { merge: true });
  if (scope === 'user') {
    try { localStorage.setItem(THEME_LOCAL_KEY, JSON.stringify(theme)); }
    catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
  }
}

/** Remove a theme override (forces fall-through to the next tier on next fetch). */
export async function deleteReportTheme(
  ownerId: string,
  scope: 'user' | 'company',
): Promise<void> {
  const ref = scope === 'company' ? companyThemeRef(ownerId) : userThemeRef(ownerId);
  await deleteDoc(ref);
  if (scope === 'user') {
    try { localStorage.removeItem(THEME_LOCAL_KEY); } catch { /* ignore */ }
  }
}

/** Read the localStorage cache (used as offline fallback during hydrate). */
export function readCachedReportTheme(): ReportTheme | null {
  try {
    const saved = localStorage.getItem(THEME_LOCAL_KEY);
    if (saved) return { ...DEFAULT_THEME, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return null;
}

/** Write the localStorage cache directly (used by the store after in-memory update). */
export function writeCachedReportTheme(theme: ReportTheme): void {
  try { localStorage.setItem(THEME_LOCAL_KEY, JSON.stringify(theme)); }
  catch (e) { if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded'); }
}

// ───────────────────────────────────────────────────────────────────────────
// Logo upload — Firebase Storage
// ───────────────────────────────────────────────────────────────────────────

/** Mirrors storage.rules — keep in sync. */
export const ALLOWED_LOGO_TYPES = new Set([
  'image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg', 'image/webp',
]);
export const MAX_LOGO_BYTES = 1024 * 1024; // 1 MB
export const LOGO_MIGRATION_KEY = 'deepatomic_logo_migrated_v1';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  /** 0–1 fraction. 0 if total is unknown. */
  progress: number;
}

function fileExt(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return '';
  return name.slice(dot + 1).toLowerCase();
}

export function isAllowedLogoFile(file: File): boolean {
  if (ALLOWED_LOGO_TYPES.has(file.type)) return true;
  // Browsers / OSes report inconsistent MIME types for valid logos:
  // SVG as 'text/xml' or 'application/octet-stream', '.jpg' as the
  // non-standard 'image/jpg', or a blank MIME from some drag/drop
  // sources. Whenever the MIME is NOT a recognized image type (empty OR
  // unrecognized), fall back to the filename extension so a genuinely
  // valid logo stays uploadable. Storage rules still enforce true MIME
  // safety server-side, and resolveUploadMime() corrects the
  // content-type before upload.
  const ext = fileExt(file.name);
  return ext === 'svg' || ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'webp';
}

function resolveUploadMime(file: File): string {
  // Trust the browser-reported MIME only when it is a recognized image
  // type. Browsers/OSes mislabel valid logos (SVG as 'text/xml' or
  // 'application/octet-stream', '.jpg' as the non-standard 'image/jpg'),
  // so when the MIME is empty OR unrecognized, derive a canonical image
  // content-type from the extension. This keeps the uploaded
  // content-type within the set the Storage rules accept.
  if (file.type && ALLOWED_LOGO_TYPES.has(file.type)) return file.type;
  const ext = fileExt(file.name);
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  // No recognized image extension either — fall back to the original
  // MIME (or octet-stream) and let the Storage rules reject it.
  return file.type || 'application/octet-stream';
}

function logoExt(mime: string, fallback = 'bin'): string {
  if (mime === 'image/svg+xml') return 'svg';
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
  const sub = mime.split('/')[1];
  return sub ? sub.replace(/[^a-z0-9]/gi, '').toLowerCase() : fallback;
}

function logoStoragePath(scope: 'user' | 'company', ownerId: string, logoId: string): string {
  return scope === 'company'
    ? `logos/companies/${ownerId}/${logoId}`
    : `logos/${ownerId}/${logoId}`;
}

const LOGO_UPLOAD_FETCH_TIMEOUT_MS = 120_000;

async function fileToBase64(
  file: File,
  onReadProgress?: (ratio: number) => void,
): Promise<string> {
  if (typeof FileReader === 'undefined') {
    onReadProgress?.(1);
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (ev) => {
      if (ev.lengthComputable && onReadProgress) {
        onReadProgress(ev.loaded / ev.total);
      }
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read logo file'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read logo file'));
    reader.readAsDataURL(file);
  });
}

/** Same-origin upload — avoids Firebase Storage CORS on localhost dev. */
async function uploadLogoViaServer(
  storagePath: string,
  file: File,
  contentType: string,
  onProgress?: (p: UploadProgress) => void,
): Promise<string> {
  onProgress?.({
    bytesTransferred: 0,
    totalBytes: file.size,
    progress: 0,
  });
  const content = await fileToBase64(file, (ratio) => {
    onProgress?.({
      bytesTransferred: Math.floor(file.size * ratio * 0.35),
      totalBytes: file.size,
      progress: ratio * 0.35,
    });
  });
  onProgress?.({
    bytesTransferred: Math.floor(file.size * 0.4),
    totalBytes: file.size,
    progress: 0.4,
  });
  onProgress?.({
    bytesTransferred: Math.floor(file.size * 0.5),
    totalBytes: file.size,
    progress: 0.5,
  });
  // UPLOAD ABORT FIX (TC3): use a same-realm AbortController timeout via
  // `createFetchAbortTimeout` rather than a `Promise.race` that left the
  // underlying fetch dangling (and never actually aborted the request).
  // `authedFetch.resolveFetchSignal` validates the signal against the
  // page's native `Request` and degrades gracefully if a runtime (undici)
  // rejects it — so this no longer risks the historical
  // "Expected signal to be an instance of AbortSignal" throw. `cancel()`
  // clears the timer on every exit path so a fast upload doesn't leak a
  // pending abort.
  const { signal, cancel } = createFetchAbortTimeout(LOGO_UPLOAD_FETCH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await authedFetch('/api/storage/upload-file', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: storagePath, content, contentType }),
      signal,
    });
  } catch (err) {
    // An aborted fetch surfaces as a DOMException 'AbortError'. Translate
    // it into a clear, user-facing timeout message; rethrow everything
    // else (network errors, etc.) so the caller can surface it verbatim.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Logo upload timed out — check your connection and try again.');
    }
    if (err instanceof Error && /aborted|abort/i.test(err.message) && signal.aborted) {
      throw new Error('Logo upload timed out — check your connection and try again.');
    }
    throw err;
  } finally {
    cancel();
  }
  const json = await res.json().catch(() => null) as { downloadUrl?: string; error?: string; detail?: string } | null;
  if (!res.ok) {
    const msg = json?.error || json?.detail || `Logo upload failed (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : 'Logo upload failed');
  }
  onProgress?.({
    bytesTransferred: file.size,
    totalBytes: file.size,
    progress: 1,
  });
  if (json?.downloadUrl) return json.downloadUrl;
  // Fallback for older servers — resolve via Storage SDK (may still work in prod).
  return getDownloadURL(ref(storage, storagePath));
}

/**
 * Upload a logo to Firebase Storage and return its download URL.
 * Validates type + size client-side; the rules enforce the same limits server-side.
 *
 * Pass `onProgress` to receive coarse upload progress (server-proxied).
 * omit for a one-shot upload.
 */
export async function uploadLogo(
  file: File,
  scope: 'user' | 'company',
  uid: string,
  companyId?: string,
  onProgress?: (p: UploadProgress) => void,
): Promise<string> {
  if (!isAllowedLogoFile(file)) {
    throw new Error(`Logo file type "${file.type || 'unknown'}" not allowed. Use PNG, SVG, JPEG, or WebP.`);
  }
  if (file.size > MAX_LOGO_BYTES) {
    throw new Error(`Logo too large (${(file.size / 1024).toFixed(0)} KB). Max 1 MB.`);
  }
  if (scope === 'company' && !companyId) {
    throw new Error('Company logo upload requires a companyId');
  }
  const ownerId = scope === 'company' ? companyId! : uid;
  const contentType = resolveUploadMime(file);
  const ext = logoExt(contentType, fileExt(file.name) || 'bin');
  const logoId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
  const storagePath = logoStoragePath(scope, ownerId, logoId);
  return uploadLogoViaServer(storagePath, file, contentType, onProgress);
}

/** Best-effort delete of a Storage-hosted logo. No-op for data: URLs and external URLs. */
export async function deleteLogo(logoUrl: string | null | undefined): Promise<void> {
  if (!logoUrl) return;
  if (logoUrl.startsWith('data:')) return;
  const storagePath = parseLogoStoragePathFromUrl(logoUrl);
  if (!storagePath) return;
  try {
    if (isHostedLogoMediaUrl(logoUrl)) {
      await authedFetch('/api/storage/delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: storagePath }),
      });
      return;
    }
    if (!logoUrl.includes('firebasestorage.googleapis.com') && !logoUrl.startsWith('gs://')) return;
    await deleteObject(ref(storage, logoUrl));
  } catch (e) {
    // File may already be gone (404) or rules may forbid this user — log and continue.
    console.warn('deleteLogo failed:', e);
  }
}

/** Decode a base64 data URL into a File. Exported for testing + migration. */
export function dataUrlToFile(dataUrl: string, filenameStem: string): File {
  const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl);
  if (!m) throw new Error('Invalid data URL');
  const mime = m[1] || 'application/octet-stream';
  const isBase64 = !!m[2];
  const data = m[3];
  let bytes: Uint8Array;
  if (isBase64) {
    const bin = atob(data);
    bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(data));
  }
  return new File([bytes], `${filenameStem}.${logoExt(mime)}`, { type: mime });
}

export interface LogoMigrationOutcome {
  migrated: boolean;
  newUrl: string | null;
  reason: 'already-migrated' | 'no-data-url' | 'oversize-cleared' | 'unsupported-type' | 'upload-error' | 'migrated';
}

/**
 * One-time migration: if the user's current theme has a data: URL logo (legacy),
 * upload it to Storage and return the replacement https URL. Sets a flag in
 * localStorage to avoid repeat attempts. Idempotent.
 */
export async function migrateDataUrlLogoIfNeeded(
  uid: string,
  currentLogoUrl: string | null,
): Promise<LogoMigrationOutcome> {
  try {
    if (localStorage.getItem(LOGO_MIGRATION_KEY) === '1') {
      return { migrated: false, newUrl: currentLogoUrl, reason: 'already-migrated' };
    }
  } catch { /* no localStorage — proceed and skip flag write at the end */ }

  if (!currentLogoUrl || !currentLogoUrl.startsWith('data:')) {
    try { localStorage.setItem(LOGO_MIGRATION_KEY, '1'); } catch { /* ignore */ }
    return { migrated: false, newUrl: currentLogoUrl, reason: 'no-data-url' };
  }

  try {
    const file = dataUrlToFile(currentLogoUrl, 'logo-migrated');
    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      // Drop unsupported legacy logos (e.g. some odd MIME) rather than block forever.
      console.warn('Legacy logo MIME not allowed; clearing:', file.type);
      try { localStorage.setItem(LOGO_MIGRATION_KEY, '1'); } catch { /* ignore */ }
      return { migrated: true, newUrl: null, reason: 'unsupported-type' };
    }
    if (file.size > MAX_LOGO_BYTES) {
      // Pre-cap data URLs could exceed 1MB — strip rather than fail forever.
      console.warn(`Legacy logo exceeds 1MB cap (${file.size} bytes); clearing.`);
      try { localStorage.setItem(LOGO_MIGRATION_KEY, '1'); } catch { /* ignore */ }
      return { migrated: true, newUrl: null, reason: 'oversize-cleared' };
    }
    const url = await uploadLogo(file, 'user', uid);
    try { localStorage.setItem(LOGO_MIGRATION_KEY, '1'); } catch { /* ignore */ }
    return { migrated: true, newUrl: url, reason: 'migrated' };
  } catch (e) {
    console.warn('Logo migration failed (will retry next session):', e);
    // Don't set the flag — we'll retry.
    return { migrated: false, newUrl: currentLogoUrl, reason: 'upload-error' };
  }
}
